'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useSession } from '@/lib/platform/auth/client';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { usePermissionsQuery } from '@/lib/platform/hooks/queries/use-permissions-query';
import { useEntitlements } from '@/lib/platform/hooks/use-entitlements';
import { getFirstAccessiblePage } from '@/lib/platform/navigation/get-first-accessible-page';

// Redirect logic for authenticated users
export default function Home() {
  const router = useRouter();
  const { data: session, isPending } = useSession();
  const { organization, isLoadingOrganization } = useOrganization();
  const { data: permissions } = usePermissionsQuery();
  const { features } = useEntitlements();

  useEffect(() => {
    if (!isPending && !isLoadingOrganization) {
      if (session?.user) {
        // CRITICAL: Wait for organization to load before redirecting
        // If no organization (!organization = loaded but no org), redirect to onboarding
        // Note: organization context returns undefined (not null) when no org exists
        if (!organization) {
          router.push('/platform/welcome');
          return;
        }

        // If organization exists, redirect to first accessible page
        if (organization) {
          const firstPage = getFirstAccessiblePage(permissions, features);
          router.push(firstPage);
        }
      } else {
        router.push('/auth/login');
      }
    }
  }, [session, isPending, organization, isLoadingOrganization, permissions, features, router]);

  // Server-rendered shell: in the HTML for crawlers (h1, descriptive copy and
  // real links — the page previously rendered empty HTML with zero outgoing
  // links), but sr-only so users don't see content flash away while the
  // client-side redirect above resolves. Visually only a quiet spinner shows
  // (same approach as the login page's SEO fallback).
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-6 text-center dark:bg-gray-900">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600 dark:border-gray-600 dark:border-t-indigo-400"
        aria-hidden="true"
      />
      <div className="sr-only">
        <h1>Valiyou — Sponsorship &amp; Marketing Value Platform</h1>
        <p>
          Valiyou helps sports clubs and commercial teams measure their attention value, create
          data-driven valuations across TV, digital, social media and matchday revenue, and manage
          sponsorship packages and proposals in one platform.
        </p>
        <nav>
          <Link href="/auth/login">Sign in</Link>
          <Link href="/auth/signup">Create an account</Link>
          <Link href="/trust-center">Trust Center</Link>
          <a href="https://valiyou.com" rel="noopener noreferrer">
            Learn more at valiyou.com
          </a>
        </nav>
      </div>
    </div>
  );
}
